import { useMemo, type ComponentProps } from "react"
import cn from "@/v2-ui/utils/utils.cn"
// One thin horizontal stacked bar that shows the *composition* of the
// fleet's current status: up / down / slow / drifting / skipped.
// Sits inside the "Overall status" KPI card so the operator can see
// the mix at a glance without scanning the seven-row sub-line
// underneath the headline number.
//
// Mute semantics: muted targets are dropped before the bar is drawn
// (ditto every other fleet aggregate on this page). The bar answers
// "what does the active fleet look like right now?" so a silenced
// service shouldn't pad a segment or shrink a percentage. The
// "Overall status" card surfaces the mute count separately under the
// headline, so the operator can still see "+3 muted" without it
// distorting the visualisation.
//
// Each surviving target lands in exactly one bucket (precedence
// below) so the segments always add up to the total -- otherwise an
// item that is both down and drifting would inflate the bar past
// 100% and the chart would lie.
//
// Precedence (most-actionable wins):
//   down   -> probe currently failing
//   slow   -> probe up but latency-flagged
//   drift  -> config drift detected on an otherwise-healthy target
//   skipped-> probe explicitly skipped
//   up     -> everything else
//
// The legend underneath only renders buckets with >0 count so the
// card stays compact when the fleet is mostly healthy.

type StatusTarget = {
  IsMuted: boolean
  LatestStatus: string
  IsSlowOutlier: boolean
  HasMetadataDrift: boolean
  HasCountDrift: boolean
}

export type UptimeStatusBreakdownProps = ComponentProps<"div"> & {
  targets: StatusTarget[]
}

type BucketKey = "down" | "slow" | "drift" | "skipped" | "up"

type Bucket = {
  key: BucketKey
  label: string
  // Tailwind class used for both the segment fill and the legend dot
  // so the legend visually keys onto the bar.
  className: string
}

// Order MUST match the visual stacking order so the bar always paints
// "down on the left, up on the right" regardless of which buckets are
// non-empty. Matches the precedence used by `bucketize` below.
const BUCKETS: ReadonlyArray<Bucket> = [
  { key: "down", label: "Down", className: "bg-destructive" },
  { key: "slow", label: "Slow", className: "bg-amber-500" },
  { key: "drift", label: "Drifting", className: "bg-violet-500" },
  { key: "skipped", label: "Skipped", className: "bg-muted-foreground/40" },
  { key: "up", label: "Up", className: "bg-emerald-500/80" },
]

function bucketize(target: StatusTarget): BucketKey {
  if (target.LatestStatus === "down") return "down"
  if (target.IsSlowOutlier) return "slow"
  if (target.HasMetadataDrift || target.HasCountDrift) return "drift"
  if (target.LatestStatus === "skipped") return "skipped"
  return "up"
}

function UptimeStatusBreakdown(props: UptimeStatusBreakdownProps) {
  const { targets, className, ...divProps } = props

  const { counts, total } = useMemo(() => {
    const c: Record<BucketKey, number> = {
      down: 0,
      slow: 0,
      drift: 0,
      skipped: 0,
      up: 0,
    }
    let active = 0
    for (const t of targets) {
      if (t.IsMuted) continue
      c[bucketize(t)] += 1
      active += 1
    }
    return { counts: c, total: active }
  }, [targets])

  if (total === 0) return null

  return (
    <div className={cn("uptime-status-breakdown", className)} {...divProps}>
      <div className="uptime-status-breakdown-bar" aria-hidden>
        {BUCKETS.map((b) => {
          const count = counts[b.key]
          if (count === 0) return null
          const pct = (100 * count) / total
          return (
            <div
              key={b.key}
              className={cn("uptime-status-breakdown-seg", b.className)}
              style={{ width: `${pct}%` }}
              title={`${b.label}: ${count}`}
            />
          )
        })}
      </div>
      <ul className="uptime-status-breakdown-legend">
        {BUCKETS.map((b) => {
          const count = counts[b.key]
          if (count === 0) return null
          return (
            <li key={b.key} className="uptime-status-breakdown-legend-item">
              <span
                className={cn(
                  "uptime-status-breakdown-legend-dot",
                  b.className,
                )}
                aria-hidden
              />
              <span className="uptime-status-breakdown-legend-label">
                {b.label}
              </span>
              <span className="uptime-status-breakdown-legend-count tabular-nums">
                {count}
              </span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

export default UptimeStatusBreakdown
